import { CATEGORY_CHANGE_SCOPE, type CategoryChangeScope } from '@klopotun/contracts';
import { useState } from 'react';
import {
  type BankOperationDetail,
  useChangeOperationCategoryMutation,
} from '../../../entities/bank-transaction/index.js';
import { useCategoriesQuery } from '../../../entities/category/index.js';
import { categoryTitleOf } from '../../../features/summary-view/index.js';
import { Button, SelectField, type SelectOption } from '../../../shared/ui/index.js';
import styles from './operation-category.module.css';

type OperationCategoryProps = {
  operation: BankOperationDetail;
};

const changedTextOf = (scope: CategoryChangeScope, changedCount: number): string =>
  scope === CATEGORY_CHANGE_SCOPE.MERCHANT
    ? `Категорію змінено для операцій продавця: ${String(changedCount)}`
    : 'Категорію змінено';

export const OperationCategory = ({ operation }: OperationCategoryProps) => {
  const [chosenCategoryId, setChosenCategoryId] = useState(operation.categoryId ?? '');
  const categories = useCategoriesQuery();
  const changeCategory = useChangeOperationCategoryMutation();

  const options: SelectOption[] = (categories.data ?? []).map((category) => ({
    value: category.id,
    label: categoryTitleOf(category),
  }));
  const isChosenOnOffer = options.some((option) => option.value === chosenCategoryId);
  const categoryId = isChosenOnOffer ? chosenCategoryId : '';
  const pendingScope = changeCategory.isPending ? changeCategory.variables.scope : null;
  const canApply = categoryId !== '' && !changeCategory.isPending;
  const isCategoryChanged = categoryId !== (operation.categoryId ?? '');
  const canApplyToOperation = canApply && isCategoryChanged;

  const apply = (scope: CategoryChangeScope): void => {
    changeCategory.mutate({ operationId: operation.id, categoryId, scope });
  };

  return (
    <section className={styles.choice}>
      <SelectField
        label="Категорія"
        name="operation-category"
        options={options}
        value={categoryId}
        placeholder="Оберіть категорію"
        disabled={categories.isPending || changeCategory.isPending}
        error={categories.isError ? categories.error.message : undefined}
        onChange={(event) => {
          changeCategory.reset();
          setChosenCategoryId(event.target.value);
        }}
      />

      {changeCategory.isSuccess && (
        <p className={styles.done} role="status">
          {changedTextOf(changeCategory.variables.scope, changeCategory.data.changedCount)}
        </p>
      )}

      {changeCategory.isError && (
        <p className={styles.error} role="alert">
          {changeCategory.error.message}
        </p>
      )}

      <div className={styles.actions}>
        <Button
          disabled={!canApplyToOperation}
          busy={pendingScope === CATEGORY_CHANGE_SCOPE.TRANSACTION}
          onClick={() => {
            apply(CATEGORY_CHANGE_SCOPE.TRANSACTION);
          }}
        >
          Лише цю операцію
        </Button>
        <Button
          variant="secondary"
          disabled={!canApply}
          busy={pendingScope === CATEGORY_CHANGE_SCOPE.MERCHANT}
          onClick={() => {
            apply(CATEGORY_CHANGE_SCOPE.MERCHANT);
          }}
        >
          Усі від цього продавця
        </Button>
      </div>
    </section>
  );
};
